import { Popover, PopoverPanel } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { StakeName } from '@/features/lobby/constants/filter-options';
import { FC, useEffect } from 'react';
import { TableFilter } from '@/services/lobby/lobbyServicesTypes';
import { GameValue } from '@/features/lobby/filters/filters';
import { BettingStructure } from '@/services/common/types/bettingStructure';
import Filters from './Filters';
import FiltersButton from '@/components/FiltersButton/FiltersButton';

interface PopoverMenuProps {
  filters: TableFilter;
  isMobileView?: boolean;
  onRangeChange: (minValue: number, maxValue: number) => void;
  onTableSizeChange: (size: number) => void;
  onEmptyTables?: () => void;
  onFullTables?: () => void;
  onFavoritesOnly?: () => void;
  onStakes?: (gameStake: StakeName) => void;
  onGameType?: (gameType: BettingStructure) => void;
  onGames?: (game: GameValue) => void;
  onResetFilters: () => void;
  onSaveFilters: () => void;
  onCloseWithoutSaving: () => void;
  children?: React.ReactNode;
}

const PopoverMenu: FC<PopoverMenuProps> = ({
  filters,
  isMobileView = false,
  onTableSizeChange,
  onEmptyTables = () => null,
  onFullTables = () => null,
  onStakes = () => null,
  onGameType = () => null,
  onResetFilters = () => null,
  onSaveFilters = () => null,
  onCloseWithoutSaving = () => null,
  children,
}) => {
  let numberOfFilters = 0;
  if (filters.games?.length) numberOfFilters++;
  if (filters.maxPlayers?.length) numberOfFilters++;
  if (filters.stakeOptions?.length) numberOfFilters++;
  if (filters.gameTypes?.length) numberOfFilters++;
  if (filters.fullTables) numberOfFilters++;
  if (filters.emptyTables) numberOfFilters++;

  useEffect(() => {
    const targetNode = document.querySelector('[data-headlessui-state]');
    if (!targetNode) return;

    const observerCallback = (mutationsList: any) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-headlessui-state') {
          if (mutation.target.getAttribute('data-headlessui-state') !== 'open') onCloseWithoutSaving();
        }
      }
    };

    const observer = new MutationObserver(observerCallback);

    observer.observe(targetNode, {
      attributes: true,
      attributeFilter: ['data-headlessui-state'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Popover id="filters" className="relative">
      <div className="flex items-center gap-2">
        {children}
        <FiltersButton numberOfFilters={numberOfFilters} />
      </div>

      <PopoverPanel className={'absolute right-0 z-50 mt-3 w-screen max-w-sm transform'}>
        {({ close }) => (
          <div className="relative">
            <div
              className={twMerge(
                'absolute right-0 rounded-xl p-[1px]',
                isMobileView ? 'w-[95vw]' : 'bg-phenom-border-glow',
              )}
            >
              <div
                className={twMerge(
                  'flex h-full w-full gap-10 rounded-xl bg-pn-violet-medium p-10',
                  isMobileView && 'flex-col bg-transparent',
                )}
              >
                <Filters
                  filters={filters}
                  onTableSizeChange={onTableSizeChange}
                  onEmptyTables={onEmptyTables}
                  onFullTables={onFullTables}
                  onStakes={onStakes}
                  onGameType={onGameType}
                  onResetFilters={onResetFilters}
                  onSaveFilters={() => {
                    onSaveFilters();
                    close();
                  }}
                  onCloseWithoutSaving={() => {
                    onCloseWithoutSaving();
                    close();
                  }}
                  isMobileView={isMobileView}
                />
              </div>
            </div>
          </div>
        )}
      </PopoverPanel>
    </Popover>
  );
};

export default PopoverMenu;
